<template>
  <div>
    <Nav></Nav>
    <b-container fluid>
      <b-row>
        <b-col class="text-left">
          <h1>{{ actualBom.name }}
            <p>ID: {{ actualBom.id }}</p>
          </h1>
          <b-button-group class="mb-3">
            <b-button variant="primary" @click="editMode = !editMode">
              <i class="fas fa-edit"></i> {{ editMode ? 'Cancelar Edición' : 'Editar' }}
            </b-button>
            <b-button variant="success" @click="saveBom" v-if="editMode" :disabled="saving">
              <b-spinner small v-if="saving"></b-spinner>
              <i class="fas fa-save" v-else></i> Guardar Orden
            </b-button>
            <b-button variant="info" @click="showAddBomLineModal" v-if="editMode">
              <i class="fas fa-plus"></i> Agregar Componente
            </b-button>
          </b-button-group>
        </b-col>
      </b-row>

      <!-- BOM Information Card -->
      <b-card class="my-2 p-1">
        <b-card-title>Información del BOM (Lista de Materiales)</b-card-title>
        <b-row>
          <b-col lg="6" md="12">
            <table class="table table-borderless text-left table-sm">
              <tbody>
                <tr>
                  <td><b>Nombre:</b></td>
                  <td>
                    <span v-if="!editMode">{{ actualBom.name }}</span>
                    <b-form-input v-else v-model="editableBom.name" size="sm"></b-form-input>
                  </td>
                </tr>
                <tr>
                  <td><b>Cantidad Base:</b></td>
                  <td>
                    <span v-if="!editMode">{{ actualBom.product_qty }}</span>
                    <b-form-input v-else v-model.number="editableBom.product_qty" type="number" step="0.01" size="sm"></b-form-input>
                  </td>
                </tr>
                <tr>
                  <td><b>Costo por unidad:</b></td>
                  <td>{{ unitPrice.toFixed(2) }}</td>
                </tr>
                <tr>
                  <td><b>Estado:</b></td>
                  <td>
                    <b-badge :variant="actualBom.active ? 'success' : 'danger'">
                      {{ actualBom.active ? 'ACTIVO' : 'INACTIVO' }}
                    </b-badge>
                  </td>
                </tr>
                <tr>
                  <td><b>Secuencia:</b></td>
                  <td>
                    <span v-if="!editMode">{{ actualBom.sequence }}</span>
                    <b-form-input v-else v-model.number="editableBom.sequence" type="number" size="sm"></b-form-input>
                  </td>
                </tr>
              </tbody>
            </table>
          </b-col>
          <b-col lg="6" md="12">
            <table class="table table-borderless text-left table-sm">
              <tbody>
                <tr>
                  <td><b>ID del Producto:</b></td>
                  <td>{{ actualBom.supply_id }}</td>
                </tr>
                <tr>
                  <td><b>Estado Activo:</b></td>
                  <td>
                    <span v-if="!editMode">{{ actualBom.active ? 'Sí' : 'No' }}</span>
                    <b-form-checkbox v-else v-model="editableBom.active" switch>
                      {{ editableBom.active ? 'Activo' : 'Inactivo' }}
                    </b-form-checkbox>
                  </td>
                </tr>
                <tr>
                  <td><b>Fecha de Creación:</b></td>
                  <td>{{ formatDate(actualBom.create_date) }}</td>
                </tr>
                <tr>
                  <td><b>Creado Por:</b></td>
                  <td>{{ actualBom.create_uid }}</td>
                </tr>
                <tr v-if="actualBom.write_date">
                  <td><b>Última Modificación:</b></td>
                  <td>{{ formatDate(actualBom.write_date) }}</td>
                </tr>
              </tbody>
            </table>
          </b-col>
        </b-row>
      </b-card>

      <!-- BOM Lines Card -->
      <b-card class="my-2">
        <b-card-title>Componentes del BOM</b-card-title>
        <b-row class="mt-3">
          <b-col>
            <b-table 
              ref="selectableTable" 
              small 
              striped 
              hover 
              :items="actualBomItems" 
              :fields="bomsLabels" 
              responsive
            >
              <template #table-busy>
                <div class="text-center text-danger my-2">
                  <b-spinner class="align-middle"></b-spinner>
                  <strong>Cargando...</strong>
                </div>
              </template>

              <template #cell(supply.name)="data">
                <span v-if="!data.item.editing">{{ data.item.supply.name }}</span>
                <b-form-select 
                  v-else 
                  v-model="data.item.supply_id" 
                  :options="suppliesOptions" 
                  size="sm"
                  @change="updateSupplyInfo(data.item)"
                ></b-form-select>
              </template>

              <template #cell(product_qty)="data">
                <span v-if="!data.item.editing">{{ data.item.product_qty }}</span>
                <b-form-input 
                  v-else 
                  v-model.number="data.item.product_qty" 
                  type="number" 
                  step="0.01" 
                  size="sm"
                  @input="updateEstPrice(data.item)"
                ></b-form-input>
              </template>

              <template #cell(sequence)="data">
                <span v-if="!data.item.editing">{{ data.item.sequence }}</span>
                <b-form-input 
                  v-else 
                  v-model.number="data.item.sequence" 
                  type="number" 
                  size="sm"
                ></b-form-input>
              </template>

              <template #cell(est_price)="data">
                {{ data.item.est_price.toFixed(2) }}
              </template>

              <template #cell(buttons)="data">
                <b-button-group size="sm" v-if="editMode">
                  <b-button 
                    variant="success" 
                    @click="saveLineEdit(data.item)" 
                    v-if="data.item.editing"
                    :disabled="data.item.saving"
                  >
                    <b-spinner small v-if="data.item.saving"></b-spinner>
                    <i class="fas fa-save" v-else></i>
                  </b-button>
                  <b-button 
                    variant="secondary" 
                    @click="cancelLineEdit(data.item)" 
                    v-if="data.item.editing"
                  >
                    <i class="fas fa-times"></i>
                  </b-button>
                  <b-button 
                    variant="primary" 
                    @click="editLine(data.item)" 
                    v-if="!data.item.editing"
                  >
                    <i class="fas fa-edit"></i>
                  </b-button>
                  <b-button 
                    variant="danger" 
                    @click="confirmDeleteLine(data.item)" 
                    v-if="!data.item.editing"
                  >
                    <i class="fas fa-trash"></i>
                  </b-button>
                </b-button-group>
                <b-button 
                  size="sm" 
                  variant="info" 
                  @click="$router.push('/supply/' + data.item.supply_id)" 
                  class="mr-1"
                  v-if="!editMode"
                >
                  Ver Insumo
                </b-button>
              </template>
            </b-table>
          </b-col>
        </b-row>
      </b-card>
    </b-container>

    <!-- Add BOM Line Modal -->
    <b-modal 
      id="modal-add-bomline" 
      title="Agregar Componente al BOM" 
      @ok="addNewBomLine"
      :ok-disabled="!newBomLine.supply_id || !newBomLine.product_qty"
    >
      <b-form>
        <b-form-group label="Insumo" label-for="supply-select">
          <b-form-select 
            id="supply-select"
            v-model="newBomLine.supply_id" 
            :options="suppliesOptions"
            required
          >
            <template #first>
              <b-form-select-option value="" disabled>-- Seleccionar Insumo --</b-form-select-option>
            </template>
          </b-form-select>
        </b-form-group>

        <b-form-group label="Cantidad" label-for="quantity-input">
          <b-form-input 
            id="quantity-input"
            v-model.number="newBomLine.product_qty" 
            type="number" 
            step="0.01"
            required
          ></b-form-input>
        </b-form-group>

        <b-form-group label="Secuencia" label-for="sequence-input">
          <b-form-input 
            id="sequence-input"
            v-model.number="newBomLine.sequence" 
            type="number"
          ></b-form-input>
        </b-form-group>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import axios from "axios";
import Nav from "../components/Nav.vue";

export default {
  name: "ProductionBom",
  components: {
    Nav
  },

  data() {
    return {
      editMode: false,
      saving: false,
      supplies: [],
      unitPrice: 0,
      actualBom: {
        id: null,
        name: null,
        supply_id: null,
        company_id: null,
        active: true,
        sequence: null,
        product_qty: null,
        create_date: null,
        write_date: null,
        create_uid: null,
        write_uid: null
      },
      editableBom: {},
      actualBomItems: [],
      newBomLine: {
        supply_id: null,
        product_qty: 1,
        sequence: 1
      },
      bomsLabels: [
        { key: "id", label: "ID" },
        { key: "supply_id", label: "ID Insumo", sortable: true },
        { key: "supply.code", label: "Código", sortable: true },
        { key: "supply.name", label: "Nombre", sortable: true },
        { key: "supply.unit", label: "UD" },
        { key: "product_qty", label: "Cantidad", sortable: true },
        { key: "sequence", label: "Secuencia", sortable: true },
        { key: "supply.price", label: "Precio Actual", sortable: true },
        { key: "est_price", label: "Total Est. Actual", sortable: true },
        { key: "buttons", label: "Acciones" }
      ]
    };
  },

  computed: {
    canEdit() {
      return this.actualBom.state === 'draft' || this.actualBom.state === 'confirmed';
    },
    
    suppliesOptions() {
      return this.supplies.map(supply => ({
        value: supply.id,
        text: `${supply.code} - ${supply.name}`
      }));
    }
  },

  watch: {
    editMode(newVal) {
      if (newVal) {
        this.editableBom = { ...this.actualBom };
        this.convertDatesToInputFormat();
      }
    }
  },

  mounted() {
    if (this.$route.params.id > 0) {
      this.actualBom.id = this.$route.params.id;
      this.loadData();
    }
  },

  methods: {
    async loadData() {
      try {
        await Promise.all([
          this.getBom(),
          this.getBomLines(),
          this.getSupplies()
        ]);
      } catch (error) {
        this.makeToast("danger", "Error", "Error al cargar los datos");
      }
    },

    getSupplies() {
      let that = this;
      return axios.get("/supplies")
        .then(function (res) {
          that.supplies = res.data.map(function (el) {
            el.name = el.code + " - " + el.name;
            return el;
          });
        }).catch(function (err) {
          that.makeToast("danger", "Error", err.response.data);
        });
    },

    getBom() {
      let that = this;
      return axios.get("/bom/" + this.actualBom.id)
        .then(function (res) {
          that.actualBom = res.data;
        }).catch(function (err) {
          that.makeToast("danger", "Error", err.response.data);
        });
    },

    getBomLines() {
      let that = this;
      return axios.get("/bom/" + this.actualBom.id + "/lines")
        .then(function (res) {
          that.actualBomItems = res.data.map(item => ({
            ...item,
            editing: false,
            saving: false,
            originalData: null
          }));
          that.calculateUnitPrice();
        }).catch(function (err) {
          that.makeToast("danger", "Error", err.response.data);
        });
    },

    calculateUnitPrice() {
      let totalPrice = 0;
      this.actualBomItems.forEach(function (el) {
        totalPrice += el.est_price;
      });
      this.unitPrice = this.actualBom.product_qty > 0 ? totalPrice / this.actualBom.product_qty : 0;
    },

    convertDatesToInputFormat() {
      if (this.editableBom.date_planned_start) {
        this.editableBom.date_planned_start = this.editableBom.date_planned_start.replace(' ', 'T').substring(0, 16);
      }
      if (this.editableBom.date_planned_finished) {
        this.editableBom.date_planned_finished = this.editableBom.date_planned_finished.replace(' ', 'T').substring(0, 16);
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return '-';
      return new Date(dateStr).toLocaleString();
    },

    getStatusVariant(state) {
      const variants = {
        'draft': 'secondary',
        'confirmed': 'warning',
        'in_progress': 'info',
        'done': 'success',
        'cancelled': 'danger'
      };
      return variants[state] || 'secondary';
    },

    getStatusText(state) {
      const texts = {
        'draft': 'BORRADOR',
        'confirmed': 'CONFIRMADO',
        'in_progress': 'EN PROCESO',
        'done': 'COMPLETADO',
        'cancelled': 'CANCELADO'
      };
      return texts[state] || state;
    },

    async saveBom() {
      this.saving = true;
      try {
        const bomData = { ...this.editableBom };
        
        // Convert datetime-local format back to server format
        if (bomData.date_planned_start) {
          bomData.date_planned_start = bomData.date_planned_start.replace('T', ' ') + ':00';
        }
        if (bomData.date_planned_finished) {
          bomData.date_planned_finished = bomData.date_planned_finished.replace('T', ' ') + ':00';
        }

        const response = await axios.put("/bom/" + this.actualBom.id, bomData);
        
        this.actualBom = response.data;
        this.editMode = false;
        this.makeToast("success", "Éxito", "Orden de manufactura actualizada correctamente");
      } catch (error) {
        this.makeToast("danger", "Error", error.response?.data || "Error al actualizar la orden");
      } finally {
        this.loadData()
        this.saving = false;
      }
    },

    editLine(item) {
      item.originalData = { ...item };
      item.editing = true;
    },

    cancelLineEdit(item) {
      // Restore original data
      Object.assign(item, item.originalData);
      item.editing = false;
      item.originalData = null;
    },

    async saveLineEdit(item) {
      item.saving = true;
      try {
        const lineData = {
          supply_id: item.supply_id,
          product_qty: item.product_qty,
          sequence: item.sequence,
          status:"active"
        };

        const response = await axios.put(`/bom/${this.actualBom.id}/line/${item.id}`, lineData);
        
        // Update the item with response data
        Object.assign(item, response.data);
        item.editing = false;
        item.originalData = null;
        
        this.calculateUnitPrice();
        this.makeToast("success", "Éxito", "Componente actualizado correctamente");
      } catch (error) {
        this.makeToast("danger", "Error", error.response?.data || "Error al actualizar el componente");
      } finally {
        item.saving = false;
        this.loadData()
      }
    },

    confirmDeleteLine(item) {
      this.$bvModal.msgBoxConfirm(
        `¿Está seguro de que desea eliminar el componente "${item.supply.name}"?`,
        {
          title: 'Confirmar eliminación',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Eliminar',
          cancelTitle: 'Cancelar',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true
        }
      ).then(value => {
        if (value) {
          this.deleteLine(item);
        }
      });
    },

    async deleteLine(item) {
      try {
        await axios.delete(`/bom/${this.actualBom.bom_id}/line/${item.id}`);
        
        // Remove from local array
        const index = this.actualBomItems.findIndex(bomItem => bomItem.id === item.id);
        if (index !== -1) {
          this.actualBomItems.splice(index, 1);
        }
        
        this.calculateUnitPrice();
        this.makeToast("success", "Éxito", "Componente eliminado correctamente");
      } catch (error) {
        this.makeToast("danger", "Error", error.response?.data || "Error al eliminar el componente");
      }
    },

    showAddBomLineModal() {
      this.newBomLine = {
        supply_id: null,
        product_qty: 1,
        sequence: this.actualBomItems.length + 1
      };
      this.$bvModal.show('modal-add-bomline');
    },

    async addNewBomLine() {
      try {
        const lineData = {
          supply_id: this.newBomLine.supply_id,
          product_qty: this.newBomLine.product_qty,
          sequence: this.newBomLine.sequence
        };

        const response = await axios.post(`/bom/${this.actualBom.id}/line`, lineData);
        
        // Add the new line to local array
        const newItem = {
          ...response.data,
          editing: false,
          saving: false,
          originalData: null
        };
        
        // Get supply information
        const supply = this.supplies.find(s => s.id === newItem.supply_id);
        if (supply) {
          newItem.supply = supply;
          newItem.est_price = supply.price * newItem.product_qty;
        }
        
        this.actualBomItems.push(newItem);
        this.calculateUnitPrice();
        this.makeToast("success", "Éxito", "Componente agregado correctamente");
      } catch (error) {
        this.makeToast("danger", "Error", error.response?.data || "Error al agregar el componente");
      } finally {
        this.loadData()
      }
    },

    updateSupplyInfo(item) {
      const supply = this.supplies.find(s => s.id === item.supply_id);
      if (supply) {
        item.supply = supply;
        this.updateEstPrice(item);
      }
    },

    updateEstPrice(item) {
      if (item.supply && item.supply.price) {
        item.est_price = item.supply.price * item.product_qty;
      }
    },

    makeToast(variant = null, title = "", text = "") {
      if (variant == "success") {
        this.$bvToast.toast(text, {
          title: title,
          variant: variant,
          solid: true,
          appendToast: true,
        });
      } else {
        this.$bvModal.msgBoxOk(text, {
          title: title,
          okVariant: variant,
          centered: true
        });
      }
    },
  },
};
</script>

<style scoped>
.table td {
  vertical-align: middle;
}

.btn-group .btn {
  margin-right: 5px;
}

.form-control[readonly] {
  background-color: #f8f9fa;
}
</style>